import { useEffect, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import SegmentedControl from '@ingka/segmented-control';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { Product, ProductDesign, ProductType } from '@inter-ikea-storageone/range';
import Title from 'ui/Typography/Title';
import { useAppDispatch } from 'ducks/hooks';
import { getFilterOptions, getUnit } from 'filters/SizeFilter/utils';
import { filterApplied, getFilterState, getProductsForGroup } from 'ducks/products';
import { isMobile } from 'utils/platform';
import styled from 'styled-components';

const MobileWrapper = styled.div`
  width: 100%;
  padding-inline: 1.5rem;
`;

const ChangeHeightFilter = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const productsInGroup = useSelector(getProductsForGroup).filter(
    p => p.type !== ProductType.BOAXEL_NON_MOUNTING_RAIL
  );
  const filterState = useSelector(getFilterState);

  const heightFilterState = filterState('sections', 'height');
  const designFilterState = filterState('sections', 'design');

  const availableHeights = getFilterOptions('height')(productsInGroup);
  const unit = getUnit(productsInGroup);

  const heights = Object.entries(availableHeights)
    .map(([size, dimensions]) => ({
      value: parseInt(size, 10),
      text: `${dimensions}`,
      unit: `${unit}`
    }))
    .toReversed();

  const controllerItems = heights.map(size => ({
    label: `${size.text} ${size.unit}`,
    value: size.value
  }));

  const currentHeight = heightFilterState?.height;
  const currentColor = designFilterState?.design || ProductDesign.WHITE;

  const filterProducts = (product: Product, size: number, color: string, types?: ProductType[]) => {
    if (!types || !types.includes(product.type)) return true;
    return product.dimensions['height'] === size && product.design === color;
  };

  const updateFilter = (selectedHeight: number) => {
    const newFilterState = { ...heightFilterState, ['height']: selectedHeight };

    const filteredProducts = productsInGroup
      .filter(p =>
        filterProducts(p, selectedHeight, currentColor, [ProductType.BOAXEL_UPRIGHT_SECTION])
      )
      .map(p => p.coreProductId);

    dispatch(
      filterApplied({
        id: 'height',
        productGroupId: 'sections',
        productIdList: filteredProducts,
        filterState: newFilterState
      })
    );
  };

  useEffect(() => {
    updateFilter(currentHeight ?? heights[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designFilterState]);

  const handleOnClick = (e: MouseEvent<HTMLButtonElement>, index: number): void =>
    updateFilter(heights[index].value);

  return (
    <>
      {isMobile() ? (
        <MobileWrapper>
          <SegmentedControl items={controllerItems} onClick={handleOnClick} fluid />
        </MobileWrapper>
      ) : (
        <>
          <Title type={ThemeFontStyleTypeEnum.headingSmall} pb="md">
            {formatMessage({ id: 'storageone.sectionHeightTitle' })}
          </Title>
          <SegmentedControl items={controllerItems} onClick={handleOnClick} fluid />
        </>
      )}
    </>
  );
};

export default ChangeHeightFilter;
